import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { extractHttpErrorMessage } from '../../../../shared/utils/http-error.util';
import { ToastService } from '../../../../shared/services/toast.service';
import { parseSkillMarkdown } from '../../../../features/skills/skill-md.util';
import { slashFromSkillName } from '../../../../features/skills/skill-slash.util';
import { SKILL_PARAM_MAX, listSkillParameters, skillPromptParameterError } from '../../../../features/skills/skill-params.util';
import { SkillsApiService } from '../../../../features/skills/skills-api.service';
import type { AgentSkill } from '../../../../features/skills/skills.models';

@Component({
  selector: 'app-create-skill',
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, TextareaModule, ButtonModule],
  templateUrl: './create-skill.html',
  styleUrl: './create-skill.css',
})
export class CreateSkill {
  private readonly api = inject(SkillsApiService);
  private readonly toast = inject(ToastService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() skillCreated = new EventEmitter<AgentSkill>();

  /** When set, the dialog updates this skill instead of creating one. Upload is create-only. */
  @Input() editing: AgentSkill | null = null;

  name = '';
  description = '';
  prompt = '';
  readonly saving = signal(false);
  readonly errorText = signal<string | null>(null);
  readonly placeholderHint =
    'Add parameter appends {{param1}}, then {{param2}}, and so on. Rename them to what you need, such as job_code.';
  readonly promptPlaceholder = 'Describe the skill';

  get slashPreview(): string {
    return slashFromSkillName(this.name);
  }

  get frontmatterLines(): string[] {
    const description = this.description.replaceAll('\r', ' ').replaceAll('\n', ' ').trim();
    return ['---', `name: ${this.slashPreview}`, `description: ${description}`, '---'];
  }

  get parameterNames(): string[] {
    return listSkillParameters(this.prompt);
  }

  onDialogShow(): void {
    if (this.editing) {
      this.name = this.editing.title || this.editing.slash;
      this.description = this.editing.description ?? '';
      this.prompt = this.editing.prompt;
    } else {
      this.name = '';
      this.description = '';
      this.prompt = '';
    }
    this.errorText.set(null);
    this.saving.set(false);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  canSave(): boolean {
    return slashFromSkillName(this.name).length >= 2 && this.prompt.trim().length > 0 && !this.saving();
  }

  addParameter(): void {
    const existing = listSkillParameters(this.prompt);
    if (existing.length >= SKILL_PARAM_MAX) {
      this.errorText.set(`A skill can have at most ${SKILL_PARAM_MAX} placeholders.`);
      return;
    }
    let index = 1;
    while (existing.includes(`param${index}`)) {
      index += 1;
    }
    const token = `{{param${index}}}`;
    const gap = this.prompt.length > 0 && !this.endsWithBreak(this.prompt) ? ' ' : '';
    this.prompt = `${this.prompt}${gap}${token}`;
    this.errorText.set(null);
  }

  openFilePicker(input: HTMLInputElement): void {
    if (this.editing || this.saving()) {
      return;
    }
    input.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';
    if (!file || this.editing || this.saving()) {
      return;
    }
    void this.fillFromFile(file);
  }

  private async fillFromFile(file: File): Promise<void> {
    const fileName = file.name || 'skill.md';
    if (!fileName.toLowerCase().endsWith('.md')) {
      this.errorText.set(`${fileName}: Only a markdown (.md) file can be added.`);
      return;
    }
    const markdown = await file.text();
    const parsed = parseSkillMarkdown(markdown);
    if (!parsed.ok) {
      this.errorText.set(`${fileName}: ${parsed.error}`);
      return;
    }
    this.name = parsed.skill.name;
    this.description = parsed.skill.description;
    this.prompt = parsed.skill.prompt;
    this.errorText.set(null);
  }

  private endsWithBreak(text: string): boolean {
    if (text.length === 0) {
      return true;
    }
    const last = text[text.length - 1];
    return last === ' ' || last === '\n' || last === '\t';
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.saving.set(true);
    this.errorText.set(null);
    const parameterError = skillPromptParameterError(this.prompt.trim());
    if (parameterError) {
      this.saving.set(false);
      this.errorText.set(parameterError);
      return;
    }
    const body = {
      name: this.name.trim(),
      description: this.description.trim(),
      prompt: this.prompt.trim(),
    };
    const request = this.editing
      ? this.api.updateSkill(this.editing.id, body)
      : this.api.createSkill(body);
    request.subscribe({
      next: (skill) => {
        this.saving.set(false);
        this.toast.success(this.editing ? `Updated /${skill.slash}` : `Saved /${skill.slash}`);
        this.skillCreated.emit(skill);
        this.close();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        const message = extractHttpErrorMessage(err, 'Could not save this skill.');
        this.errorText.set(message);
        this.toast.error(message);
      },
    });
  }
}
